/**
 * Stream Parser Utility
 *
 * Extracts complete screen objects from partial JSON streaming response.
 * Used for progressive rendering during AI generation.
 */

/**
 * Parsed screen from the stream (minimal structure for preview)
 */
export interface ParsedScreen {
  id?: string;
  screenType?: string;
  title?: string;
  blocks: Array<{
    type: string;
    text?: string;
    items?: string[];
  }>;
}

/**
 * Stream parsing result
 */
export interface StreamParseResult {
  screens: ParsedScreen[];
  title?: string;
  isComplete: boolean;
  progress: number; // 0-1 based on estimated completion
}

/**
 * Attempt to extract complete screens from partial JSON stream
 *
 * The AI response is structured like:
 * {"id": "...", "title": "...", "screens": [{...}, {...}], ...}
 *
 * We try to parse individual screen objects as they complete.
 */
export function tryParseScreens(text: string): ParsedScreen[] {
  const screens: ParsedScreen[] = [];

  // Try to find complete screen objects
  // Pattern: look for {...} blocks that contain screenType or blocks
  const screenRegex = /\{[^{}]*"screenType"\s*:\s*"[^"]+"\s*,[^{}]*"blocks"\s*:\s*\[[^\]]*\][^{}]*\}/g;

  // Try a more lenient approach: find "screens": [...] array content
  const screensArrayMatch = text.match(/"screens"\s*:\s*\[/);
  if (!screensArrayMatch) {
    return screens;
  }

  const screensStart = screensArrayMatch.index! + screensArrayMatch[0].length;

  // Extract content after "screens": [
  let remaining = text.slice(screensStart);
  let depth = 1;
  let currentScreen = '';
  let screenStart = 0;

  for (let i = 0; i < remaining.length; i++) {
    const char = remaining[i];

    if (char === '{') {
      if (depth === 1) {
        screenStart = i;
      }
      depth++;
    } else if (char === '}') {
      depth--;
      if (depth === 1) {
        // We completed a screen object
        currentScreen = remaining.slice(screenStart, i + 1);
        try {
          const parsed = JSON.parse(currentScreen);
          if (parsed && (parsed.screenType || parsed.blocks)) {
            screens.push(normalizeScreen(parsed));
          }
        } catch {
          // Incomplete JSON, skip
        }
      }
    } else if (char === ']' && depth === 1) {
      // End of screens array
      break;
    }
  }

  return screens;
}

/**
 * Extract title from partial stream
 */
export function tryParseTitle(text: string): string | undefined {
  const titleMatch = text.match(/"title"\s*:\s*"([^"]+)"/);
  return titleMatch ? titleMatch[1] : undefined;
}

/**
 * Estimate generation progress based on stream content
 */
export function estimateProgress(text: string, expectedScreens: number): number {
  // Quick heuristics:
  // - If we see "screens": [ we're past initial setup (~10%)
  // - Count complete screen objects
  // - If we see "globalMetadata" we're near the end (~90%)
  // - If we see closing braces we're done

  if (!text) return 0;

  let progress = 0;

  // Has the response started?
  if (text.includes('{')) progress = 0.05;

  // Has title been generated?
  if (text.includes('"title"')) progress = 0.1;

  // Has screens array started?
  if (text.includes('"screens"')) progress = 0.15;

  // Count screens
  const screens = tryParseScreens(text);
  if (screens.length > 0 && expectedScreens > 0) {
    progress = 0.15 + (screens.length / expectedScreens) * 0.7;
  }

  // Has globalMetadata started?
  if (text.includes('"globalMetadata"')) {
    progress = Math.max(progress, 0.9);
  }

  // Is it complete? (ends with } or })
  if (text.trim().endsWith('}')) {
    // Try to parse the whole thing
    try {
      JSON.parse(text);
      progress = 1.0;
    } catch {
      // Not complete yet
    }
  }

  return Math.min(progress, 1.0);
}

/**
 * Parse complete presentation from full stream
 */
export function parseStreamResult(text: string): StreamParseResult {
  let presentation: any = null;
  let isComplete = false;

  try {
    // First try direct parse
    presentation = JSON.parse(text);
    isComplete = true;
  } catch {
    // Try extracting JSON from text
    const jsonMatch = text.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      try {
        presentation = JSON.parse(jsonMatch[0]);
        isComplete = true;
      } catch {
        // Still not parseable
      }
    }
  }

  if (isComplete && presentation) {
    return {
      screens: (presentation.screens || []).map(normalizeScreen),
      title: presentation.title,
      isComplete: true,
      progress: 1.0,
    };
  }

  // Partial parse
  const screens = tryParseScreens(text);
  const title = tryParseTitle(text);

  return {
    screens,
    title,
    isComplete: false,
    progress: estimateProgress(text, 5), // Default to 5 expected screens
  };
}

/**
 * Normalize a screen object for preview
 */
function normalizeScreen(screen: any): ParsedScreen {
  const blocks = (screen.blocks || [])
    .map((b: any) => ({
      type: b.type || 'unknown',
      text: b.text ? cleanBlockText(b.text) : undefined,
      items: b.items ? b.items.map((item: string) => cleanBlockText(item)).filter(Boolean) : undefined,
    }))
    .filter((b: any) => {
      // Filter out blocks with no useful content
      if (b.type === 'image-placeholder') return false; // Skip image placeholders in preview
      if (!b.text && (!b.items || b.items.length === 0)) return false;
      return true;
    });

  // Extract title from first title block
  const titleBlock = blocks.find((b: { type: string }) => b.type === 'title');

  return {
    id: screen.id,
    screenType: screen.screenType,
    title: titleBlock?.text || screen.screenType || 'Screen',
    blocks,
  };
}

/**
 * Clean block text - filter out internal IDs, technical content, and strip markdown
 */
function cleanBlockText(text: string): string {
  if (!text) return '';

  let cleaned = text.trim();

  // Filter out block IDs like "block-13", "block-0", etc.
  if (/^block-\d+$/i.test(cleaned)) return '';

  // Filter out other technical patterns
  if (isInternalContent(cleaned)) return '';

  // Strip markdown formatting
  cleaned = stripMarkdown(cleaned);

  return cleaned;
}

/**
 * Strip visual markdown formatting from text (for preview display only)
 * NOTE: Links are preserved - only visual formatting is stripped
 */
function stripMarkdown(text: string): string {
  let result = text;

  // Remove bold **text** or __text__
  result = result.replace(/\*\*([^*]+)\*\*/g, '$1');
  result = result.replace(/__([^_]+)__/g, '$1');

  // Remove italic *text* or _text_ (but not inside words)
  result = result.replace(/(?<!\w)\*([^*]+)\*(?!\w)/g, '$1');
  result = result.replace(/(?<!\w)_([^_]+)_(?!\w)/g, '$1');

  // Remove inline code `code`
  result = result.replace(/`([^`]+)`/g, '$1');

  // Remove headers # ## ### etc
  result = result.replace(/^#{1,6}\s+/gm, '');

  // Remove horizontal rules
  result = result.replace(/^[-*_]{3,}\s*$/gm, '');

  // Clean up any double spaces
  result = result.replace(/\s{2,}/g, ' ');

  return result.trim();
}

/**
 * Get a display-friendly title for a screen
 */
export function getScreenDisplayTitle(screen: ParsedScreen, index: number): string {
  if (screen.title && screen.title.length > 0) {
    // Truncate long titles
    return screen.title.length > 30
      ? screen.title.slice(0, 30) + '...'
      : screen.title;
  }

  // Fall back to screen type
  if (screen.screenType) {
    const typeLabels: Record<string, string> = {
      'title-slide': 'Title',
      'section-header': 'Section',
      'content': 'Content',
      'two-column': 'Two Column',
      'image-focus': 'Image',
      'quote-slide': 'Quote',
      'closing': 'Closing',
    };
    return typeLabels[screen.screenType] || screen.screenType;
  }

  return `Screen ${index + 1}`;
}

// ============================================
// LIVE TYPING SUPPORT
// ============================================

/**
 * Content currently being typed on the active slide
 */
export interface TypingContent {
  screenIndex: number;
  screenType?: string;
  title: string;
  subtitle: string;
  bodyLines: string[];
  bulletItems: string[];
  isTypingTitle: boolean;
  isTypingSubtitle: boolean;
  isTypingBody: boolean;
  isTypingBullets: boolean;
}

/**
 * Full streaming state for live preview
 */
export interface LiveStreamState {
  presentationTitle?: string;
  suggestedThemeId?: string;
  completedScreens: ParsedScreen[];
  currentScreen: TypingContent | null;
  totalExpectedScreens: number;
  isComplete: boolean;
}

/**
 * Extract live typing state from streaming text
 * This parses both complete screens AND the partial screen being typed
 */
export function extractLiveStreamState(
  text: string,
  expectedScreens: number
): LiveStreamState {
  const completedScreens = tryParseScreens(text);
  const presentationTitle = tryParseTitle(text);
  const suggestedThemeId = tryParseSuggestedTheme(text);

  // Check if generation is complete
  let isComplete = false;
  try {
    JSON.parse(text);
    isComplete = true;
  } catch {
    // Not complete
  }

  // Find the partial screen being typed
  const currentScreen = extractPartialScreen(text, completedScreens.length);

  return {
    presentationTitle,
    suggestedThemeId,
    completedScreens,
    currentScreen,
    totalExpectedScreens: expectedScreens,
    isComplete,
  };
}

/**
 * Extract suggested theme ID from partial stream
 */
function tryParseSuggestedTheme(text: string): string | undefined {
  const match = text.match(/"suggestedThemeId"\s*:\s*"([^"]+)"/);
  return match ? match[1] : undefined;
}

/**
 * Extract the partial screen currently being typed
 */
function extractPartialScreen(text: string, completedCount: number): TypingContent | null {
  // Find where screens array starts
  const screensMatch = text.match(/"screens"\s*:\s*\[/);
  if (!screensMatch) return null;

  const screensStart = screensMatch.index! + screensMatch[0].length;
  let remaining = text.slice(screensStart);

  // Skip past completed screens
  let depth = 1;
  let screensSkipped = 0;
  let i = 0;

  for (; i < remaining.length && screensSkipped < completedCount; i++) {
    const char = remaining[i];
    if (char === '{') depth++;
    else if (char === '}') {
      depth--;
      if (depth === 1) screensSkipped++;
    }
  }

  // Now extract the partial screen content
  const partialContent = remaining.slice(i);

  // Look for screenType
  const screenTypeMatch = partialContent.match(/"screenType"\s*:\s*"([^"]+)"/);
  const screenType = screenTypeMatch ? screenTypeMatch[1] : undefined;

  // Extract partial block contents
  const title = extractPartialText(partialContent, 'title');
  const subtitle = extractPartialText(partialContent, 'subtitle');
  const body = extractPartialText(partialContent, 'body');
  const bullets = extractPartialBullets(partialContent);

  // Determine what's currently being typed
  const hasTitle = title.length > 0;
  const hasSubtitle = subtitle.length > 0;
  const hasBody = body.length > 0;
  const hasBullets = bullets.length > 0;

  // Check if we're in the middle of typing something
  const isTypingTitle = hasTitle && !hasSubtitle && !hasBody && !hasBullets;
  const isTypingSubtitle = hasSubtitle && !hasBody && !hasBullets;
  const isTypingBody = hasBody && !hasBullets;
  const isTypingBullets = hasBullets;

  if (!hasTitle && !screenType) {
    return null; // Haven't started this screen yet
  }

  return {
    screenIndex: completedCount,
    screenType,
    title,
    subtitle,
    bodyLines: body ? [body] : [],
    bulletItems: bullets,
    isTypingTitle,
    isTypingSubtitle,
    isTypingBody,
    isTypingBullets,
  };
}

/**
 * Extract partial text from a block type
 * Handles incomplete JSON like: {"type": "title", "text": "Hello Wor
 */
function extractPartialText(content: string, blockType: string): string {
  // Pattern: "type": "blockType" ... "text": "captured text
  // The text might be incomplete (no closing quote)

  const patterns = [
    // Complete text value
    new RegExp(`"type"\\s*:\\s*"${blockType}"[^}]*"text"\\s*:\\s*"([^"]*)"`, 's'),
    // Incomplete text value (no closing quote yet)
    new RegExp(`"type"\\s*:\\s*"${blockType}"[^}]*"text"\\s*:\\s*"([^"]*)$`, 's'),
  ];

  for (const pattern of patterns) {
    const match = content.match(pattern);
    if (match) {
      const cleaned = cleanExtractedText(match[1]);
      // Only return if it looks like real content
      if (cleaned.length > 0) {
        return cleaned;
      }
    }
  }

  return '';
}

/**
 * Extract partial bullet items from bullet-list block
 */
function extractPartialBullets(content: string): string[] {
  const items: string[] = [];

  // Look for "type": "bullet-list" ... "items": [...]
  const bulletMatch = content.match(/"type"\s*:\s*"bullet-list"[^}]*"items"\s*:\s*\[/s);
  if (!bulletMatch) return items;

  const itemsStart = bulletMatch.index! + bulletMatch[0].length;
  const itemsContent = content.slice(itemsStart);

  // Extract complete items
  const completeItemsMatch = itemsContent.match(/"([^"]*)"/g);
  if (completeItemsMatch) {
    for (const item of completeItemsMatch) {
      const cleaned = cleanExtractedText(item.slice(1, -1));
      if (cleaned.length > 0) {
        items.push(cleaned);
      }
    }
  }

  // Check for partial item at the end
  const partialMatch = itemsContent.match(/,\s*"([^"]*)$/);
  if (partialMatch) {
    const cleaned = cleanExtractedText(partialMatch[1]);
    if (cleaned.length > 0) {
      items.push(cleaned);
    }
  }

  return items;
}

/**
 * Unescape JSON string escape sequences
 */
function unescapeJsonString(str: string): string {
  return str
    .replace(/\\n/g, '\n')
    .replace(/\\t/g, '\t')
    .replace(/\\"/g, '"')
    .replace(/\\\\/g, '\\');
}

/**
 * Check if text looks like internal/technical content that shouldn't be shown
 */
function isInternalContent(text: string): boolean {
  const trimmed = text.trim().toLowerCase();

  // Single technical words that shouldn't display
  const technicalWords = [
    // JSON structure
    'type', 'text', 'items', 'blocks', 'block', 'screentype', 'id', 'screen',
    // Block types
    'title-slide', 'section-header', 'content', 'two-column', 'image-focus',
    'quote-slide', 'closing', 'bullet-list', 'body', 'title', 'subtitle',
    'numbered-list', 'quote', 'callout', 'speaker-notes', 'image-placeholder',
    // Image-related
    'image', 'photo', 'illustration', 'infographic', 'imagestyle', 'aspectratio',
    'suggestedkeywords', 'description', '16:9', '4:3', '1:1',
    'imagesource', 'generate', 'url', 'imageurl', 'resolvedimageurl', 'poster',
    // Other schema fields
    'level', 'emphasis', 'normal', 'strong', 'subtle', 'startnumber',
    'attribution', 'callouttype', 'info', 'tip', 'warning',
    'backgroundhint', 'light', 'dark',
  ];

  if (technicalWords.includes(trimmed)) {
    return true;
  }

  // Patterns for JSON-like content
  const technicalPatterns = [
    /^"?[a-z_-]+"?\s*:/i, // Property name with colon
    /^\{|\}$|^\[|\]$/,     // Lone brackets
    /^"[a-z_-]+"$/i,       // Just a quoted identifier
    /^\d+:\d+$/,           // Aspect ratios like 16:9
    /^block-[\w-]*$/i,     // Block IDs: "block-13", UUID-form "block-a1b2c3d4", or bare "block-" partials mid-stream
    /^screen-\d+$/i,       // Screen IDs
    /^mmo-\d+$/i,          // MMO IDs
  ];

  for (const pattern of technicalPatterns) {
    if (pattern.test(trimmed)) {
      return true;
    }
  }

  // Skip very short fragments that are likely partial JSON
  if (trimmed.length < 3 && /[{}\[\]:,"]/.test(trimmed)) {
    return true;
  }

  return false;
}

/**
 * Clean extracted text - remove JSON artifacts and filter internal content
 */
function cleanExtractedText(text: string): string {
  if (!text) return '';

  let cleaned = unescapeJsonString(text);

  // Remove leading/trailing JSON syntax artifacts. Don't strip [ or ]
  // from either end — those are legitimate markdown-link delimiters in body
  // text (e.g. "[Hugging Face](https://...)") and the live preview needs to
  // see them intact so renderInlineMarkdown can parse and style the link.
  cleaned = cleaned.replace(/^[\s{}:,"]+/, '').replace(/[\s{}:,"]+$/, '');

  // If after cleaning it looks internal, return empty
  if (isInternalContent(cleaned)) {
    return '';
  }

  return cleaned;
}
